// imports
import { Button, Radio, Grid, RadioGroup, Typography, FormControlLabel } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';

// components
import { Alert } from 'components/common/Alert';
import DataLoader from 'components/common/DataLoader';
import AddCardForm from 'components/main/CheckoutForm/AddCardForm';
import CurrentPlan from './CurrentPlan';
import PaymentCards from './PaymentCards';
import AddBankAccountForm from './AddBankAccountForm';

// interfaces, helpers, constants
import {
  ADD_NEW_PAYMENT_METHOD_TEXT,
  CHANGE_PAYMENT_METHOD_TEXT,
  PAYMENT_METHOD_TEXT,
  PAYMENT_TYPE_ACH_LABEL,
  PAYMENT_TYPE_CARD_LABEL,
  ENV,
} from 'constants/index';
import { useAuthContext } from 'contexts/AuthContext';
import { StripePaymentMethodType, useGetPaymentMethodsLazyQuery } from 'generated/graphql';
import { isAdmin, isStaff } from 'lib/helper';
import { DarkGreyCardBox, LightGreyCardBox, PaymentMethodContainer } from 'theme/styleComponent';

const PaymentMethods: FC = () => {
  const [openForm, setOpenForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [paymentType, setPaymentType] = useState<StripePaymentMethodType>(StripePaymentMethodType.Card);
  const { userRoles } = useAuthContext();
  const [stripePromise] = useState(() => loadStripe(ENV.STRIPE_KEY));

  const [fetchPaymentMethods, { data, loading, refetch }] = useGetPaymentMethodsLazyQuery({
    onError: (error) => {
      Alert.error(error.message);
      setFormLoading(false);
    },
  });

  const isStaffUser = useMemo(() => isStaff(userRoles), [userRoles]);
  const paymentMethods = data?.getPaymentMethods?.paymentMethods || [];
  const hasPaymentMethods = paymentMethods.length > 0;
  const admin = isAdmin(userRoles);

  useEffect(() => {
    admin && fetchPaymentMethods();
  }, [fetchPaymentMethods, admin]);

  const handleRefetch = () => {
    refetch();
    setOpenForm(false);
  };

  return (
    <PaymentMethodContainer container>
      <Grid item xs={12} md={8}>
        <CurrentPlan />

        {!isStaffUser && (
          <DarkGreyCardBox sx={{ mt: 4 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {PAYMENT_METHOD_TEXT}
            </Typography>

            {loading ? (
              <DataLoader columns={12} rows={2} />
            ) : (
              <Fragment>
                <PaymentCards paymentMethods={paymentMethods} handleSuccess={handleRefetch} />
                <Button variant="text" onClick={() => setOpenForm(true)} sx={{ mt: 2 }}>
                  {hasPaymentMethods ? CHANGE_PAYMENT_METHOD_TEXT : ADD_NEW_PAYMENT_METHOD_TEXT}
                </Button>
              </Fragment>
            )}

            {openForm && (
              <LightGreyCardBox mt={2} pt={2}>
                <RadioGroup
                  row
                  value={paymentType}
                  onChange={(_, value) => setPaymentType(value as StripePaymentMethodType)}
                  sx={{ mb: 2 }}>
                  <FormControlLabel
                    value={StripePaymentMethodType.Card}
                    control={<Radio />}
                    label={PAYMENT_TYPE_CARD_LABEL}
                  />
                  <FormControlLabel
                    value={StripePaymentMethodType.Ach}
                    control={<Radio />}
                    label={PAYMENT_TYPE_ACH_LABEL}
                  />
                </RadioGroup>
                <Elements stripe={stripePromise}>
                  {paymentType === StripePaymentMethodType.Card ? (
                    <AddCardForm
                      setFormLoading={setFormLoading}
                      formLoading={formLoading}
                      onComplete={handleRefetch}
                      onCancel={() => setOpenForm(false)}
                    />
                  ) : (
                    <AddBankAccountForm
                      setFormLoading={setFormLoading}
                      formLoading={formLoading}
                      onComplete={handleRefetch}
                      onCancel={() => setOpenForm(false)}
                    />
                  )}
                </Elements>
              </LightGreyCardBox>
            )}
          </DarkGreyCardBox>
        )}
      </Grid>
    </PaymentMethodContainer>
  );
};

export default PaymentMethods;
